import * as React from 'react';
import { Wifi, WifiOff, AlertCircle, CheckCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useNetworkStatus } from '@/hooks/offline/useNetworkStatus';
import { useSyncStatusPolling } from '@/hooks/useSyncStatus';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';

export const OfflineIndicator: React.FC = () => {
  const { isOnline, connectionQuality } = useNetworkStatus();
  const syncStatus = useSyncStatusPolling();
  const [showDetails, setShowDetails] = React.useState(false);

  // Calculate total unsynced
  const totalUnsynced = syncStatus.pending + syncStatus.failed;

  // Always show for debugging - you can comment this out later
  // if (isOnline && totalUnsynced === 0) {
  //   return null;
  // }

  const getStatusColor = () => {
    if (!isOnline) return 'bg-red-500';
    if (syncStatus.failed > 0) return 'bg-amber-500';
    if (syncStatus.pending > 0) return 'bg-blue-500';
    return 'bg-green-500';
  };

  const getStatusIcon = () => {
    if (!isOnline) return <WifiOff className="h-4 w-4" />;
    if (syncStatus.failed > 0) return <AlertCircle className="h-4 w-4" />;
    if (syncStatus.pending > 0) return <Wifi className="h-4 w-4" />;
    return <CheckCircle className="h-4 w-4" />;
  };

  const getStatusText = () => {
    if (!isOnline) return 'Offline';

    // Show total unsynced count
    if (totalUnsynced > 0) {
      const parts = [];
      if (syncStatus.pending > 0) parts.push(`${syncStatus.pending} pending`);
      if (syncStatus.failed > 0) parts.push(`${syncStatus.failed} failed`);
      return parts.join(', ');
    }

    return 'All synced';
  };

  return (
    <Popover open={showDetails} onOpenChange={setShowDetails}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className={cn(
            'h-8 gap-2 px-3',
            !isOnline && 'text-red-600 dark:text-red-400'
          )}
        >
          <div className={cn('h-2 w-2 rounded-full', getStatusColor())} />
          {getStatusIcon()}
          <span className="text-sm">{getStatusText()}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80" align="end">
        <div className="space-y-4">
          <div>
            <h4 className="font-semibold mb-2">Sync Status</h4>

            {/* Unsynced items summary */}
            {totalUnsynced > 0 && (
              <div className="mb-3 rounded-lg bg-blue-50 dark:bg-blue-900/20 p-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-blue-900 dark:text-blue-100">
                    Unsynced Items
                  </span>
                  <Badge variant="secondary" className="text-base">
                    {totalUnsynced}
                  </Badge>
                </div>
              </div>
            )}

            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Connection</span>
                <Badge variant={isOnline ? 'default' : 'destructive'}>
                  {isOnline ? 'Online' : 'Offline'}
                </Badge>
              </div>

              {isOnline && connectionQuality.type !== 'unknown' && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Quality</span>
                  <span className="font-medium">{connectionQuality.type.toUpperCase()}</span>
                </div>
              )}

              {syncStatus.pending > 0 && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Pending</span>
                  <Badge variant="secondary">
                    {syncStatus.pending} operations
                  </Badge>
                </div>
              )}

              {syncStatus.failed > 0 && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Failed</span>
                  <Badge variant="destructive">
                    {syncStatus.failed} operations
                  </Badge>
                </div>
              )}

              {syncStatus.synced > 0 && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Synced</span>
                  <Badge variant="default">
                    {syncStatus.synced} operations
                  </Badge>
                </div>
              )}
            </div>
          </div>

          {!isOnline && (
            <div className="rounded-lg bg-muted p-3">
              <p className="text-sm text-muted-foreground">
                You're working offline. Changes will be synced automatically when you reconnect.
              </p>
            </div>
          )}

          {connectionQuality.saveData && (
            <div className="rounded-lg bg-amber-50 dark:bg-amber-900/20 p-3">
              <p className="text-sm text-amber-600 dark:text-amber-400">
                Data saver is enabled. Automatic sync is paused.
              </p>
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

// Minimal offline badge for mobile/compact views
export const OfflineBadge: React.FC = () => {
  const { isOnline } = useNetworkStatus();
  const syncStatus = useSyncStatusPolling();

  if (isOnline && syncStatus.pending === 0) {
    return null;
  }

  return (
    <Badge
      variant={isOnline ? 'secondary' : 'destructive'}
      className="gap-1"
    >
      {isOnline ? (
        <>
          <Wifi className="h-3 w-3" />
          {syncStatus.pending} pending
        </>
      ) : (
        <>
          <WifiOff className="h-3 w-3" />
          Offline
        </>
      )}
    </Badge>
  );
};